import type { CSSProperties } from 'react';
import { color } from '../styles/theme';
import type { PermissionMatrix } from '../api/client';
import { ONLY_AN_OWNER } from '../api/access';

/**
 * The tick boxes behind every role, and behind one person's Custom set.
 *
 * Two rules live here as well as in the API, so the screen never offers a
 * combination the server would refuse: ticking a box also ticks what it needs
 * (an export needs the names tick), and unticking one clears whatever needed
 * it. `applyToggle` is pure so the role editor and the per-person panel share
 * it without sharing state.
 */

export const samePermissions = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((x) => b.includes(x));

export function applyToggle(
  matrix: PermissionMatrix,
  current: readonly string[],
  key: string,
): string[] {
  const order = matrix.permissions.map((p) => p.key);
  const requires = (k: string) => matrix.permissions.find((p) => p.key === k)?.requires ?? [];
  const cur = new Set(current);

  if (cur.has(key)) {
    cur.delete(key);
    // Drop anything that needed the box just cleared, and so on down.
    let changed = true;
    while (changed) {
      changed = false;
      for (const p of matrix.permissions) {
        if (cur.has(p.key) && p.requires.some((r) => !cur.has(r))) {
          cur.delete(p.key);
          changed = true;
        }
      }
    }
  } else {
    const add = (k: string) => {
      cur.add(k);
      for (const r of requires(k)) if (!cur.has(r)) add(r);
    };
    add(key);
  }

  return order.filter((k) => cur.has(k));
}

export function PermissionGrid({
  matrix,
  value,
  editable,
  onChange,
  subject,
  locked = [],
  withheld = [],
  style,
}: {
  matrix: PermissionMatrix;
  value: readonly string[];
  editable: boolean;
  onChange?: (next: string[]) => void;
  /** Who or what the boxes describe — read out before each label. */
  subject: string;
  /** Always ticked and never editable, e.g. what an Owner keeps regardless. */
  locked?: readonly string[];
  /**
   * Boxes the signed-in person may not tick, because only an owner gives what
   * they do not hold themself (access.ts `beyondActor`). Unticking stays open.
   */
  withheld?: readonly string[];
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(264px, 1fr))',
        gap: '0 26px',
        alignItems: 'start',
        opacity: editable ? 1 : 0.78,
        ...style,
      }}
    >
      {matrix.groups.map((g) => {
        const rows = matrix.permissions.filter((p) => p.group === g);
        if (rows.length === 0) return null;
        return (
          <div key={g} style={{ marginBottom: 10 }}>
            <p
              style={{
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: color.muted,
                margin: '6px 0 3px 6px',
              }}
            >
              {g}
            </p>
            {rows.map((p) => {
              const held = locked.includes(p.key);
              const on = held || value.includes(p.key);
              const ownerOnly = !on && withheld.includes(p.key);
              const canEdit = editable && !held && !ownerOnly;
              return (
                <label
                  key={p.key}
                  className={canEdit ? 'hov-tint2' : undefined}
                  title={editable && ownerOnly ? `Only an owner can give “${p.label}”: you do not have it yourself.` : undefined}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '17px 1fr',
                    gap: 9,
                    alignItems: 'start',
                    padding: '6px 8px 6px 6px',
                    borderRadius: 8,
                    cursor: canEdit ? 'pointer' : 'default',
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={`${subject}: ${p.label}`}
                    checked={on}
                    disabled={!canEdit}
                    onChange={() => onChange?.(applyToggle(matrix, value, p.key))}
                    style={{
                      width: 16,
                      height: 16,
                      marginTop: 1,
                      accentColor: color.blue,
                      cursor: canEdit ? 'pointer' : 'default',
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 13,
                        lineHeight: '18px',
                        fontWeight: on ? 700 : 500,
                        color: on ? color.ink : color.muted,
                      }}
                    >
                      {p.label}
                    </span>
                    <span style={{ display: 'block', fontSize: 11, color: color.muted }}>
                      {p.where}
                      {editable && ownerOnly && ` · ${ONLY_AN_OWNER}`}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
